import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Select, {
  type FormatOptionLabelMeta,
  type StylesConfig
} from '@databases/atlaskit/Select';
import FieldName from '@databases/field-name/FieldName';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { type DatabaseField } from '@databases/types/database';
import type { DatabaseCoverFieldType } from '@databases/types/database/view';
import React, { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { getCompactSelectStyles } from './compact-select-styles.ts';

export type CustomFieldPickerOption = {
  customType: DatabaseCoverFieldType.NONE;
  label: string;
};

type FieldPickerOption = DatabaseField | CustomFieldPickerOption;

function isOptionDatabaseField(value: FieldPickerOption) {
  return 'id' in value;
}

const inlineSelectStyles = xcss({
  maxWidth: '120px'
});

const disabledOptionStyles = xcss({
  opacity: 0.4
});

const getOptionValue = (value: FieldPickerOption) => {
  if (!isOptionDatabaseField(value)) {
    return value.customType;
  }
  return value.id;
};

const getOptionLabel = (value: FieldPickerOption) => {
  if (!isOptionDatabaseField(value)) {
    return value.label;
  }
  return value.name;
};

const styles: StylesConfig<FieldPickerOption> = {
  control: (base) => ({
    ...base,
    font: token('font.body', '14px')
  }),
  input: (base) => ({
    ...base,
    font: token('font.body', '14px')
  }),
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
  option: (base) => ({
    ...base,
    font: token('font.body', '14px')
  })
};

const compactSelectStyles = getCompactSelectStyles<FieldPickerOption>();

const compactStyles: StylesConfig<FieldPickerOption> = {
  ...styles,
  ...compactSelectStyles,
  control: (base, state) => ({
    ...(compactSelectStyles.control?.(base, state) ?? base),
    ...(!state.isDisabled && {
      borderColor: state.isFocused ? token('color.border.focused') : token('color.border')
    }),
    font: token('font.body', '14px')
  })
};

function validateOption(
  option: FieldPickerOption | undefined,
  isRequired: boolean | undefined
): boolean {
  return !(isRequired && !option);
}

interface FieldPickerProps {
  fields?: ReadonlyArray<DatabaseField>;
  value?: FieldPickerOption | undefined;
  onChange: (value: DatabaseField | undefined) => void;
  onCustomOptionChange?: (value: CustomFieldPickerOption) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isRequired?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  spacing?: 'compact' | 'default';
  isDisabled?: boolean;
  isInvalid?: boolean;
  // boolean to determine if picker is set inline with other elements
  isPickerInline?: boolean;
  customOptions?: CustomFieldPickerOption[];
  defaultValue?: FieldPickerOption;
  ariaLabel?: string;
  inputId?: string;
  autoSelectFirst?: boolean;
  iconSpacing?: 'space.050' | 'space.100';
  getDisabledReason?: (field: DatabaseField) => string | null;
}

/**
 * Renders a select box that allows to select a field from the given database.
 */
const FieldPicker: React.FunctionComponent<FieldPickerProps> = ({
  value,
  fields,
  onChange,
  onCustomOptionChange,
  onFocus,
  onBlur,
  autoFocus,
  isRequired,
  isClearable,
  isLoading,
  placeholder,
  spacing,
  isDisabled = false,
  isInvalid,
  isPickerInline = false,
  customOptions = [],
  defaultValue,
  ariaLabel,
  inputId,
  autoSelectFirst = false,
  iconSpacing,
  getDisabledReason
}) => {
  const { formatMessage } = useIntl();

  const formatOptionLabel = (
    value: FieldPickerOption,
    { context }: FormatOptionLabelMeta<FieldPickerOption>
  ): React.ReactNode => {
    if (!isOptionDatabaseField(value)) {
      return value.label;
    }

    const disabledReason =
      context === 'menu' ? (getDisabledReason?.(value) ?? null) : null;
    const fieldName = (
      <Box
        xcss={[
          isPickerInline && inlineSelectStyles,
          Boolean(disabledReason) && disabledOptionStyles
        ]}
      >
        <FieldName field={value} showIcon iconSpacing={iconSpacing} />
      </Box>
    );

    if (!disabledReason) {
      return fieldName;
    }

    return (
      <Tooltip content={disabledReason} position="left">
        {(tooltipProps) => <div {...tooltipProps}>{fieldName}</div>}
      </Tooltip>
    );
  };

  const isOptionDisabled = (option: FieldPickerOption) =>
    isOptionDatabaseField(option) && Boolean(getDisabledReason?.(option));

  useEffect(() => {
    // If a different database is selected, reset the value
    if (
      value &&
      isOptionDatabaseField(value) &&
      (!fields || !fields.some((field) => field.id === value.id))
    ) {
      onChange(undefined);
    }
  }, [fields, onChange, value]);

  useEffect(() => {
    if (autoSelectFirst && value == null && fields && fields.length > 0) {
      onChange(fields[0]);
    }
  }, [autoSelectFirst, value, fields, onChange]);

  const handleChange = useCallback(
    (val: FieldPickerOption | null) => {
      if (val && !isOptionDatabaseField(val)) {
        onCustomOptionChange?.(val);
      } else {
        onChange(val ?? undefined);
      }
    },
    [onChange, onCustomOptionChange]
  );

  return (
    <ValidationStatus
      error={
        validateOption(value, isRequired)
          ? undefined
          : formatMessage(messages['validation.required-field'])
      }
    >
      {(props) => (
        <Select<FieldPickerOption>
          {...props}
          value={value ?? defaultValue}
          onChange={handleChange}
          isClearable={isClearable}
          isDisabled={isDisabled || !fields}
          formatOptionLabel={formatOptionLabel}
          options={[...(fields ?? []), ...customOptions]}
          getOptionValue={getOptionValue}
          getOptionLabel={getOptionLabel}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          isLoading={isLoading}
          placeholder={placeholder}
          spacing={spacing}
          styles={spacing === 'compact' ? compactStyles : styles}
          isInvalid={props.isInvalid || isInvalid}
          isOptionDisabled={getDisabledReason ? isOptionDisabled : undefined}
          classNamePrefix="field-picker"
          aria-label={ariaLabel}
          inputId={inputId}
          shouldPreventEscapePropagation
        />
      )}
    </ValidationStatus>
  );
};

export default FieldPicker;
